import { AutoPipeAssignerSettings, PipeUsage, StartFrom } from './types';

// Per viewer and per project, in this browser only: a shared setting would belong on the project.
export interface RememberedInputs extends Partial<AutoPipeAssignerSettings> {
  usage: Record<string, PipeUsage>;
}

const PIPE_USAGES: PipeUsage[] = ['none', 'level', 'borehole'];

const START_FROM: StartFrom[] = ['current', 'lowest'];

const NUMERIC_FIELDS = ['pumpPressureBar', 'throughput', 'targetUCS', 'curingDays', 'allowancePercent'] as const;

const storageKey = (projectId: string) => `autoPipeAssigner:${projectId}`;

const finiteNumber = (value: unknown): number | undefined => (typeof value === 'number' && Number.isFinite(value) ? value : undefined);

// Anything malformed, or naming a pipe that has since been deleted, is dropped.
export const sanitiseRememberedInputs = (stored: unknown, pipeIds: string[]): RememberedInputs => {
  const raw = (stored && typeof stored === 'object' ? stored : {}) as Record<string, unknown>;
  const known = new Set(pipeIds);
  const usage = (raw.usage && typeof raw.usage === 'object' ? raw.usage : {}) as Record<string, unknown>;
  const numbers = NUMERIC_FIELDS.reduce<Partial<AutoPipeAssignerSettings>>((acc, field) => {
    const value = finiteNumber(raw[field]);
    return value === undefined ? acc : { ...acc, [field]: value };
  }, {});

  return {
    ...numbers,
    rheologyDataSetId: typeof raw.rheologyDataSetId === 'string' ? raw.rheologyDataSetId : undefined,
    startFrom: START_FROM.includes(raw.startFrom as StartFrom) ? raw.startFrom as StartFrom : undefined,
    excludedPipeIds: Array.isArray(raw.excludedPipeIds)
      ? raw.excludedPipeIds.filter((pipeId): pipeId is string => typeof pipeId === 'string' && known.has(pipeId))
      : undefined,
    usage: Object.entries(usage).reduce<Record<string, PipeUsage>>((acc, [pipeId, value]) => (
      known.has(pipeId) && PIPE_USAGES.includes(value as PipeUsage) ? { ...acc, [pipeId]: value as PipeUsage } : acc
    ), {}),
  };
};

export const readRememberedInputs = (projectId: string, pipeIds: string[]): RememberedInputs => {
  try {
    const stored = localStorage.getItem(storageKey(projectId));
    return sanitiseRememberedInputs(stored ? JSON.parse(stored) : undefined, pipeIds);
  } catch {
    return { usage: {} };
  }
};

export const rememberInputs = (projectId: string, inputs: RememberedInputs) => {
  try {
    localStorage.setItem(storageKey(projectId), JSON.stringify(inputs));
  } catch {
    // Storage unavailable: the inputs simply aren't remembered.
  }
};
